import React from 'react';
import { Box, Typography, Button, IconButton, Select, MenuItem, TextField, Autocomplete, Chip, CircularProgress, Tooltip } from '@mui/material';
import {
  CardGiftcard, Delete, Add, Remove, PersonAdd, PersonSearch, CheckCircle, GroupAdd, ForwardToInbox, Groups, ReceiptLong, Redeem, CurrencyRupee,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  addGvLine, updateGvLine, removeGvLine, setGvDelivery, setGvBuyerEmail, setGvFocusLine,
} from '../../../store/giftVoucherSaleSlice';
import { GV, EMAIL_RE, inr, localTotals, recipientsComplete, typeAllowsValue } from './gvUtils';

const capSx = { fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem', textTransform: 'uppercase' };

function SectionHead({ label, right }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
      <Typography sx={capSx}>{label}</Typography>
      {right}
    </Box>
  );
}

function QtyStepper({ value, onChange, min = 1 }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0', overflow: 'hidden', width: 'fit-content' }}>
      <Button onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} sx={{ minWidth: 30, p: 0, height: 30, color: '#64748B' }} aria-label="Less">
        <Remove sx={{ fontSize: 15 }} />
      </Button>
      <Typography sx={{ width: 34, textAlign: 'center', fontSize: '0.85rem', fontWeight: 800 }}>{value}</Typography>
      <Button onClick={() => onChange(value + 1)} sx={{ minWidth: 30, p: 0, height: 30, color: '#6366F1' }} aria-label="More">
        <Add sx={{ fontSize: 15 }} />
      </Button>
    </Box>
  );
}

const fieldSx = { height: 32, borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, bgcolor: '#FFF' };

// Fixed-value types get a plain dropdown; open-amount types get a dropdown you can also type into.
function ValuePicker({ type, value, onChange }) {
  const denoms = (type?.denominations || []).map(Number);
  if (!type?.allow_custom_amount) {
    return (
      <Select size="small" value={denoms.includes(Number(value)) ? Number(value) : ''} displayEmpty
        onChange={(e) => onChange(e.target.value)} renderValue={(v) => (v === '' ? 'Value' : inr(v))}
        sx={{ ...fieldSx, width: '100%' }} inputProps={{ 'aria-label': 'Voucher value' }}>
        {denoms.map((v) => <MenuItem key={v} value={v} sx={{ fontSize: '0.82rem' }}>{inr(v)}</MenuItem>)}
      </Select>
    );
  }
  const text = value === '' || value == null ? '' : String(value);
  return (
    <Autocomplete freeSolo disableClearable size="small" options={denoms.map(String)} value={text} inputValue={text}
      onInputChange={(e, v, reason) => { if (reason !== 'reset') onChange(v.replace(/[^\d.]/g, '')); }}
      onChange={(e, v) => onChange(Number(v) || '')}
      renderOption={(props, o) => { const { key, ...rest } = props; return <li key={key} {...rest} style={{ fontSize: '0.82rem' }}>{inr(o)}</li>; }}
      sx={{ width: '100%', '& .MuiOutlinedInput-root': { ...fieldSx, py: '0 !important', pl: '8px !important' } }}
      renderInput={(params) => (
        <TextField {...params} placeholder="Amount"
          inputProps={{ ...params.inputProps, inputMode: 'decimal', 'aria-label': 'Voucher value' }}
          InputProps={{ ...params.InputProps, startAdornment: <CurrencyRupee sx={{ fontSize: 14, color: '#64748B' }} /> }} />
      )} />
  );
}

// One slim row: type · value · qty · line total · delete. Below ~460px of panel width it folds into two rows.
function VoucherLine({ line, types, delivery, focused, onEditPeople }) {
  const dispatch = useDispatch();
  const type = types.find((t) => t.id === line.typeId);
  const ok = recipientsComplete(line);
  const named = line.recipients.filter((r) => r.name?.trim() || r.email?.trim());
  const set = (changes) => dispatch(updateGvLine({ key: line.key, changes }));
  const overMax = type?.max_quantity_per_sale && Number(line.qty) > Number(type.max_quantity_per_sale);
  const badValue = type?.allow_custom_amount && line.value !== '' && !typeAllowsValue(type, line.value);

  return (
    <Box onClick={() => dispatch(setGvFocusLine(line.key))}
      sx={{
        containerType: 'inline-size', border: '1px solid', borderColor: focused ? '#4F46E5' : '#E2E8F0', borderRadius: '8px', px: 1, py: 0.75,
        bgcolor: '#FFF', display: 'flex', flexDirection: 'column', gap: 0.75, boxShadow: focused ? '0 0 0 3px rgba(79,70,229,0.08)' : 'none',
        '&:hover': { borderColor: focused ? '#4F46E5' : '#C7D2FE' },
      }}>
      <Box sx={{
        display: 'grid', alignItems: 'center', columnGap: 1, rowGap: 0.75,
        gridTemplateColumns: 'auto minmax(0, 1fr) 118px auto 84px auto',
        gridTemplateAreas: '"icon type value qty total del"',
        '@container (max-width: 460px)': {
          gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
          gridTemplateAreas: '"icon type type del" "value value qty total"',
        },
      }}>
        <CardGiftcard sx={{ gridArea: 'icon', fontSize: 18, color: GV.main }} />
        <Select size="small" value={type ? line.typeId : ''} displayEmpty
          onChange={(e) => {
            const t = types.find((x) => x.id === e.target.value);
            set({ typeId: t.id, value: Number(t.denominations[0] || t.min_amount || 0) });
          }}
          sx={{ ...fieldSx, gridArea: 'type', minWidth: 0 }}
          inputProps={{ 'aria-label': 'Voucher type' }}>
          {!type && <MenuItem value="" disabled>Type no longer on sale</MenuItem>}
          {types.map((t) => <MenuItem key={t.id} value={t.id} sx={{ fontSize: '0.82rem' }}>{t.name}</MenuItem>)}
        </Select>
        <Box sx={{ gridArea: 'value', minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          <ValuePicker type={type} value={line.value} onChange={(v) => set({ value: v })} />
        </Box>
        <Box sx={{ gridArea: 'qty' }}>
          <QtyStepper value={Number(line.qty)} onChange={(q) => set({ qty: q })} />
        </Box>
        <Typography noWrap sx={{ gridArea: 'total', textAlign: 'right', fontSize: '0.9rem', fontWeight: 900, color: '#1E293B' }}>
          {inr((Number(line.value) || 0) * Number(line.qty))}
        </Typography>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); dispatch(removeGvLine(line.key)); }} aria-label="Remove line"
          sx={{ gridArea: 'del', color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {(overMax || badValue) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {badValue && (
            <Chip size="small" label={`${type.min_amount ? inr(type.min_amount) : '₹1'} – ${type.max_amount ? inr(type.max_amount) : 'no limit'} for this type`}
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#FEF2F2', color: '#B91C1C', borderRadius: '6px' }} />
          )}
          {overMax && (
            <Chip size="small" label={`Over ${type.max_quantity_per_sale} · needs approval`}
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#FFFBEB', color: '#B45309', borderRadius: '6px' }} />
          )}
        </Box>
      )}

      {delivery === 'individual' && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.4, borderRadius: '6px', flexWrap: 'wrap', fontSize: '0.7rem', fontWeight: 700,
          bgcolor: ok ? '#F0FDF4' : '#FFFBEB', color: ok ? '#16A34A' : '#92400E', border: '1px solid', borderColor: ok ? '#BBF7D0' : '#FDE68A',
        }}>
          {ok ? <CheckCircle sx={{ fontSize: 16 }} /> : <GroupAdd sx={{ fontSize: 16 }} />}
          <span>People {Math.min(named.length, line.qty)}/{line.qty}</span>
          <Typography noWrap sx={{ flex: 1, minWidth: 100, fontSize: '0.68rem', fontWeight: 600, color: '#64748B' }}>
            {named.map((r) => (r.name || r.email).split(' ')[0]).join(', ') || 'Nobody added yet'}
          </Typography>
          <Button size="small" onClick={(e) => { e.stopPropagation(); onEditPeople(line.key); }}
            sx={{
              minWidth: 0, height: 26, px: 1, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.66rem',
              ...(ok ? { border: '1px solid #E2E8F0', color: '#64748B', bgcolor: '#FFF' } : { border: '1px solid #C7D2FE', color: '#4F46E5', bgcolor: '#EEF2FF' }),
            }}>
            {ok ? 'Edit people' : 'Add people'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function GiftVoucherSalePanel({ types, isLoadingTypes, onPickBuyer, onEditPeople }) {
  const dispatch = useDispatch();
  const sale = useSelector((s) => s.giftVoucherSale);
  const totals = localTotals(sale);
  const emailBad = !!sale.buyerEmail && !EMAIL_RE.test(sale.buyerEmail.trim());

  const addLine = () => {
    const t = types[0];
    if (!t) return;
    dispatch(addGvLine({ typeId: t.id, value: Number(t.denominations[0] || t.min_amount || 0), qty: 1 }));
  };

  return (
    <Box sx={{
      flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.75,
      '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
    }}>
      <Box component="section">
        <SectionHead label="Buyer" right={!sale.buyer && <Chip size="small" label="Required" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, bgcolor: '#FEF2F2', color: '#DC2626', borderRadius: '6px' }} />} />
        {sale.buyer ? (
          <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '10px', p: 1.5, bgcolor: '#FFF', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#4F46E5', color: '#FFF', fontWeight: 800, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {(sale.buyer.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{sale.buyer.name}</Typography>
                <Typography noWrap sx={{ fontSize: '0.7rem', color: '#64748B' }}>{sale.buyer.phone_number}</Typography>
              </Box>
              <Button size="small" onClick={onPickBuyer}
                sx={{ minWidth: 0, height: 28, px: 1.25, borderRadius: '6px', textTransform: 'none', fontWeight: 700, fontSize: '0.7rem', border: '1px solid #E2E8F0', color: '#64748B' }}>
                Change
              </Button>
            </Box>
            <Box>
              <Typography component="label" htmlFor="gv-buyer-email" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B' }}>
                Email for delivery <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
              </Typography>
              <TextField id="gv-buyer-email" fullWidth size="small" type="email" value={sale.buyerEmail} placeholder="name@company.com" autoComplete="off"
                onChange={(e) => dispatch(setGvBuyerEmail(e.target.value))} error={emailBad}
                sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.82rem' } }} />
              <Typography sx={{ fontSize: '0.66rem', color: emailBad ? '#DC2626' : '#94A3B8', mt: 0.5 }}>
                {emailBad ? 'Enter a valid email' : sale.buyer.email ? 'Saved on the customer. Changing it here updates the customer.' : 'This customer has no email yet. It will be saved on the customer.'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ border: '2px dashed #E2E8F0', borderRadius: '10px', bgcolor: '#FFF', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.75, py: 2.25, px: 1.5 }}>
            <PersonAdd sx={{ fontSize: 28, color: '#CBD5E1' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748B' }}>Who is paying for the vouchers?</Typography>
            <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8' }}>The buyer must be a registered customer with an email.</Typography>
            <Button variant="contained" disableElevation startIcon={<PersonSearch />} onClick={onPickBuyer}
              sx={{ mt: 0.5, bgcolor: '#4F46E5', borderRadius: '8px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338CA' } }}>
              Select buyer (F2)
            </Button>
          </Box>
        )}
      </Box>

      <Box component="section">
        <SectionHead label="Vouchers" right={<Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94A3B8' }}>{totals.count} voucher{totals.count !== 1 ? 's' : ''} · {inr(totals.face)}</Typography>} />
        {isLoadingTypes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={20} sx={{ color: '#94A3B8' }} /></Box>
        ) : !types.length ? (
          <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed #E2E8F0', borderRadius: '10px' }}>
            <CardGiftcard sx={{ fontSize: 32, color: '#E2E8F0' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94A3B8' }}>No gift vouchers are on sale at this store</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {sale.lines.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 2.5, border: '1px dashed #E2E8F0', borderRadius: '10px', bgcolor: '#FFF' }}>
                <Redeem sx={{ fontSize: 32, color: '#E2E8F0' }} />
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#94A3B8' }}>No vouchers added yet</Typography>
              </Box>
            )}
            {sale.lines.map((l) => (
              <VoucherLine key={l.key} line={l} types={types} delivery={sale.delivery} focused={sale.focusLineKey === l.key} onEditPeople={onEditPeople} />
            ))}
            <Button fullWidth onClick={addLine} startIcon={<Add />}
              sx={{ height: 42, border: '1.5px dashed #CBD5E1', borderRadius: '10px', bgcolor: '#FFF', color: '#4F46E5', fontWeight: 800, fontSize: '0.8rem', textTransform: 'none', '&:hover': { bgcolor: '#EEF2FF', borderColor: '#C7D2FE' } }}>
              Add voucher line
            </Button>
          </Box>
        )}
      </Box>

      <Box component="section">
        <SectionHead label="Delivery" />
        <Box role="radiogroup" aria-label="Delivery" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden', bgcolor: '#F8FAFC' }}>
          {[
            { v: 'buyer', label: 'Send all to buyer', icon: <ForwardToInbox sx={{ fontSize: 18 }} /> },
            { v: 'individual', label: 'Email each person', icon: <Groups sx={{ fontSize: 18 }} /> },
          ].map((o, i) => {
            const on = sale.delivery === o.v;
            return (
              <Tooltip key={o.v} title={o.label} disableHoverListener>
                <Button role="radio" aria-checked={on} onClick={() => dispatch(setGvDelivery(o.v))} startIcon={o.icon}
                  sx={{
                    py: 1.1, borderRadius: 0, textTransform: 'none', fontWeight: 800, fontSize: '0.78rem', minWidth: 0,
                    borderLeft: i ? '1px solid #E2E8F0' : 'none', bgcolor: on ? '#EEF2FF' : 'transparent', color: on ? '#4F46E5' : '#64748B',
                    '& .MuiButton-startIcon': { mr: 0.75 }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                  {o.label}
                </Button>
              </Tooltip>
            );
          })}
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 1 }}>
          {sale.delivery === 'buyer'
            ? 'The buyer gets one email with every code and PIN (PDF + CSV). Names you add are included in the file.'
            : 'Each person gets their own code and PIN. The buyer gets a summary without PINs.'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.1, borderRadius: '8px', bgcolor: GV.bg, border: `1px solid ${GV.border}`, color: GV.text, fontSize: '0.72rem', lineHeight: 1.4 }}>
        <ReceiptLong sx={{ fontSize: 17, mt: '1px' }} />
        <span>Voucher sales are <b>not a taxable supply</b>. The receipt is titled “Gift Voucher Sale Receipt – Not a Tax Invoice”. GST is charged when the voucher is used on a bill.</span>
      </Box>
    </Box>
  );
}
